import * as React from 'react';
import { Switch, Redirect } from 'react-router';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useAppSelector } from '@common/hooks';
import {
  networks as networksSelector,
  selected as selectedOrganisation,
} from '@modules/organisation/selectors/organisation';
import useNetworkComponentCheck from '@common/hooks/permissions/use-network-component-check';
import Route, { getRedirect } from '../../common/components/route';
import RouteNotFound from '../../common/components/route-not-found';
import Permission from '../../common/components/permission';
import { SubNavigationBar } from '../../common/components/navigation-bar';
import EmployeesContainer from './containers/employees';
import CommunicationContainer from './containers/communication';
import FlexchangeContainer from './containers/flexchange';
import OnboardingContainer from './containers/onboarding';
import {
  EComponentTypes,
  ENetworkSpecificComponentTypes,
  EPermissions,
} from '../../common/definitions';
import * as userSelector from '../core/selectors/logged-user';
import ELearningAnalytics from './components/elearning/elearning';
import GeneralStatisticsContainer from './containers/general';

require('./styles.scss');

const AnalyticsContainer = ({ match: { params }, permissions }) => {
  const { t } = useTranslation();

  const hasPermission = (names) => names.some((name) => permissions.includes(name));

  const baseUrl = params.networkId ? `/networks/${params.networkId}` : '/admin'; // /networks/${params.networkId}
  const basePath = params.networkId ? '/networks/:networkId' : '/admin'; // /networks/:networkId

  const redirects = [
    {
      permissions: [
        EPermissions.ORGANISATION_USER_STATISTICS_VIEW,
        EPermissions.NETWORK_USER_STATISTICS_VIEW,
      ],
      path: null,
    },
    {
      permissions: [
        EPermissions.ORGANISATION_COMMUNICATION_STATISTICS_VIEW,
        EPermissions.NETWORK_COMMUNICATION_STATISTICS_VIEW,
      ],
      path: `${baseUrl}/analytics/communication`,
    },
    {
      permissions: [
        EPermissions.ORGANISATION_EXCHANGE_STATISTICS_VIEW,
        EPermissions.NETWORK_EXCHANGE_STATISTICS_VIEW,
      ],
      path: `${baseUrl}/analytics/flexchange`,
    },
    {
      permissions: [
        EPermissions.ORGANISATION_ONBOARDING_STATISTICS,
        EPermissions.NETWORK_ONBOARDING_STATISTICS,
      ],
      path: `${baseUrl}/analytics/onboarding`,
    },
    {
      path: `${baseUrl}/analytics/highfives`,
    },
  ];

  const redirect = getRedirect(redirects, hasPermission);

  const networks = useAppSelector(networksSelector);

  const flexchangeEnabled = useNetworkComponentCheck(
    ENetworkSpecificComponentTypes.FLEXCHANGE,
    networks
  );
  const canSeeFlexchange =
    hasPermission([
      EPermissions.ORGANISATION_EXCHANGE_STATISTICS_VIEW,
      EPermissions.NETWORK_EXCHANGE_STATISTICS_VIEW,
    ]) && flexchangeEnabled;

  const organisation = useAppSelector(selectedOrganisation);

  const isOnboardingEnabled = organisation.enabled_components.includes(
    EComponentTypes.ONBOARDING
  );
  const isELearningEnabled = organisation.enabled_components.includes(
    EComponentTypes.ACADEMY
  );

  return (
    <>
      <SubNavigationBar title={t('core:tab_statistics')}>
        <Permission
          name={[
            EPermissions.ORGANISATION_USER_STATISTICS_VIEW,
            EPermissions.NETWORK_USER_STATISTICS_VIEW,
          ]}
        >
          <SubNavigationBar.Item
            exact
            icon="group"
            path={`${baseUrl}/analytics`}
            title={t('network:statistics_navigation_employees')}
          />
        </Permission>
        <Permission
          name={[
            EPermissions.ORGANISATION_COMMUNICATION_STATISTICS_VIEW,
            EPermissions.NETWORK_COMMUNICATION_STATISTICS_VIEW,
          ]}
        >
          <SubNavigationBar.Item
            exact
            icon="send"
            path={`${baseUrl}/analytics/communication`}
            title={t('network:statistics_navigation_communication')}
          />
        </Permission>
        {canSeeFlexchange && (
          <SubNavigationBar.Item
            exact
            icon="sync_alt"
            path={`${baseUrl}/analytics/flexchange`}
            title={t('network:statistics_navigation_flexchange')}
          />
        )}
        {isOnboardingEnabled && (
          <Permission
            name={[
              EPermissions.ORGANISATION_ONBOARDING_STATISTICS,
              EPermissions.NETWORK_ONBOARDING_STATISTICS,
            ]}
          >
            <SubNavigationBar.Item
              exact
              icon="work"
              path={`${baseUrl}/analytics/onboarding`}
              title={t('network:statistics_navigation_onboarding')}
            />
          </Permission>
        )}

        {isELearningEnabled && (
          <Permission
            name={[
              EPermissions.ORGANISATION_ACADEMY_COURSES_STATISTICS,
              EPermissions.NETWORK_ELEARNING_STATISTICS,
            ]}
          >
            <SubNavigationBar.Item
              exact
              icon="school"
              path={`${baseUrl}/analytics/elearning`}
              title={t('core:tab_eLearning')}
            />
          </Permission>
        )}

        {!params.networkId && (
          <SubNavigationBar.Item
            exact
            icon="analytics"
            path={`${baseUrl}/analytics/general`}
            title={t('core:tab_general')}
          />
        )}
      </SubNavigationBar>

      {/** Routes */}
      <Switch>
        {hasPermission([
          EPermissions.ORGANISATION_USER_STATISTICS_VIEW,
          EPermissions.NETWORK_USER_STATISTICS_VIEW,
        ]) && (
          <Route
            exact
            path={`${basePath}/analytics`}
            component={EmployeesContainer}
          />
        )}
        {hasPermission([
          EPermissions.ORGANISATION_COMMUNICATION_STATISTICS_VIEW,
          EPermissions.NETWORK_COMMUNICATION_STATISTICS_VIEW,
        ]) && (
          <Route
            exact
            path={`${basePath}/analytics/communication`}
            component={CommunicationContainer}
          />
        )}
        {hasPermission([
          EPermissions.ORGANISATION_ONBOARDING_STATISTICS,
          EPermissions.NETWORK_ONBOARDING_STATISTICS,
        ]) && (
          <Route
            exact
            path={`${basePath}/analytics/onboarding`}
            component={OnboardingContainer}
          />
        )}
        {hasPermission([
          EPermissions.ORGANISATION_ACADEMY_COURSES_STATISTICS,
          EPermissions.NETWORK_ELEARNING_STATISTICS,
        ]) && (
          <Route
            exact
            path={`${basePath}/analytics/elearning`}
            component={ELearningAnalytics}
          />
        )}

        <Route
          exact
          path={`${basePath}/analytics/general`}
          component={GeneralStatisticsContainer}
        />

        {canSeeFlexchange && (
          <Route
            exact
            path={`${basePath}/analytics/flexchange`}
            component={FlexchangeContainer}
          />
        )}

        {redirect && (
          <Redirect exact from={`${baseUrl}/analytics`} to={redirect} />
        )}
        <Route component={RouteNotFound} />
      </Switch>
    </>
  );
};

const mapStateToProps = (state) => ({
  permissions: userSelector.permissions(state),
});

export default connect(mapStateToProps)(AnalyticsContainer);
